import type { ApiEnvironment } from 'src/shared/utils/environment';

import type { MaterialRegionOrPaint } from '../customizationCategories';

export enum DesignerPartner {
  YES_COLOURS = 'yes_colours',
  PELLA = 'pella',
  REALTOR = 'realtor',
  HARDIE = 'hardie',
  NONE = 'none',
}

export enum MaterialCalloutType {
  SHOP = 'shop',
  APPOINTMENT = 'appointment',
}

type PartnerOrgIds = { [OrgId: string]: DesignerPartner };

interface BasePartnerMetadata {
  partner: DesignerPartner;
  configuration: DesignerPartnerConfiguration;
  orgId?: number;
  displayName?: string;
  logoUrl?: string;
  logoUrlInvert?: string;
}

export interface AssociatedPartnerMetadata extends BasePartnerMetadata {
  partner: Exclude<DesignerPartner, DesignerPartner.NONE>;
}

export interface NoPartnerMetadata extends BasePartnerMetadata {
  partner: DesignerPartner.NONE;
}

/**
 * Metadata for a partner association that includes both partner information
 * that comes from the backend (name, logo) and the hard-coded configuration.
 */
export type PartnerMetadata = AssociatedPartnerMetadata | NoPartnerMetadata;

/**
 * Hard-coded mapping of partner org IDs in our different API environments. The
 * org ID is mapped to the `DesignerPartner` enum, which can then be used to
 * retrieve the hard-coded partner configuration from the different mappings for
 * product catalog products, the lead form, etc.
 *
 * @remarks This is a stop-gap while we define how partner configuration will
 * actually work and move most or all the hard-coded configuration to the
 * backend.
 */
export const DESIGNER_PARTNER_MAP: Record<
  ApiEnvironment,
  PartnerOrgIds | null
> = {
  production: {
    '655657': DesignerPartner.YES_COLOURS,
    '752013': DesignerPartner.PELLA, // MEOW-5511
    '651112': DesignerPartner.REALTOR,
    '488993': DesignerPartner.HARDIE,
  },
  staging: {
    '32046': DesignerPartner.YES_COLOURS,
    '32047': DesignerPartner.PELLA, // MEOW-5511
    '13705': DesignerPartner.HARDIE,
  },
  sandbox: null,
  mock: null,
} as const;

export interface DesignerPartnerConfiguration {
  brandType?: MaterialRegionOrPaint;
  /**
   * The type of material callout to display when `manufacturerUrl` is present
   * on partner products. This field must be configured for material callouts to
   * show up alongside products in the partner experience.
   */
  calloutType?: MaterialCalloutType;
  defaultLeadFormConfiguration: number;
  additionalLinkouts: string[];
}

/**
 * The primary hard-coded configuration for each partner. This has a 1-to-1
 * mapping with the partner org.
 */
export const PARTNER_CONFIGURATION_MAP: Record<
  DesignerPartner,
  DesignerPartnerConfiguration
> = {
  [DesignerPartner.PELLA]: {
    brandType: 'windows',
    calloutType: MaterialCalloutType.APPOINTMENT,
    defaultLeadFormConfiguration: 3,
    additionalLinkouts: [
      'pella_demystifying_windows',
      'pella_home_consultation',
      'pella_find_retailer',
      'pella_design_trends',
    ],
  },
  [DesignerPartner.REALTOR]: {
    defaultLeadFormConfiguration: 2,
    additionalLinkouts: [],
  },
  [DesignerPartner.HARDIE]: {
    brandType: 'walls',
    defaultLeadFormConfiguration: 2,
    additionalLinkouts: [],
  },
  [DesignerPartner.NONE]: {
    defaultLeadFormConfiguration: 1,
    additionalLinkouts: [],
  },
  [DesignerPartner.YES_COLOURS]: {
    brandType: 'paint',
    calloutType: MaterialCalloutType.SHOP,
    defaultLeadFormConfiguration: 3,
    additionalLinkouts: [],
  },
};

export const NO_PARTNER_METADATA: NoPartnerMetadata = {
  partner: DesignerPartner.NONE,
  configuration: PARTNER_CONFIGURATION_MAP[DesignerPartner.NONE],
};
